/**
 * Navigation utilities for handling external redirects safely.
 *
 * When using window.location.href to navigate to external URLs (like Stripe),
 * pending fetch requests get aborted and can throw errors. This utility
 * sets a global flag that error boundaries can check to suppress these
 * navigation-induced errors.
 */

// Global flag to track if navigation is in progress
// Using window object for cross-component access
declare global {
  interface Window {
    __isNavigatingExternal?: boolean;
  }
}

// Timeout to reset the navigation flag if navigation fails (5 seconds)
// This prevents errors from being permanently suppressed if navigation fails
const NAVIGATION_TIMEOUT_MS = 5000;

/**
 * Check if external navigation is in progress.
 * Use this in error boundaries to suppress navigation-induced errors.
 */
export function isNavigatingExternal(): boolean {
  if (typeof window === 'undefined') return false;
  return window.__isNavigatingExternal === true;
}

/**
 * Reset the external navigation flag.
 * Call this when navigation is cancelled or fails and local overlay state is reset.
 * This keeps the global flag in sync with component state.
 */
export function resetNavigationFlag(): void {
  if (typeof window === 'undefined') return;
  window.__isNavigatingExternal = false;
}

/**
 * Navigate to an external URL safely.
 * Sets a flag to suppress error boundary during navigation.
 * The flag is automatically reset after a timeout to handle failed navigations.
 */
export function navigateToExternal(url: string): void {
  if (typeof window === 'undefined') return;

  // Set navigation flag BEFORE navigating
  window.__isNavigatingExternal = true;

  // Safety net: Reset flag after timeout if navigation fails
  // If navigation succeeds, this timeout never runs (new page context)
  // If navigation fails (network error, CSP block, etc.), this restores error handling
  setTimeout(() => {
    window.__isNavigatingExternal = false;
  }, NAVIGATION_TIMEOUT_MS);

  // Navigate
  window.location.href = url;
}

/**
 * Reload the page safely.
 * Sets a flag to suppress error boundary during reload.
 * The flag is automatically reset after a timeout to handle failed reloads.
 */
export function reloadPageSafely(): void {
  if (typeof window === 'undefined') return;

  window.__isNavigatingExternal = true;

  // Safety net: Reset flag after timeout if reload fails
  setTimeout(() => {
    window.__isNavigatingExternal = false;
  }, NAVIGATION_TIMEOUT_MS);

  window.location.reload();
}
